import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useAsync } from "react-use";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { ROLE_DISPLAY_NAMES } from "../../utils/permissions";

type User = {
  id: string;
  name: string;
  email: string;
  fbos: { id: string; name: string }[];
  fbo_employees: { fbo_id: string; _role: string }[];
};

type Props = {};

export const SuperUserAdmin: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [editingUser, setEditingUser] = React.useState<string | null>(null);
  const [users, setUsers] = React.useState<User[]>([]);
  const { fbos } = useActiveFBOs();
  const [actionsOnSave, setActionsOnSave] = React.useState<(() => void)[]>([]);
  const [fboFilter, setFBOFilter] = React.useState<string>("");
  const [userFilter, setUserFilter] = React.useState<string>("");

  const { loading: loadingUsers } = useAsync(async () => {
    const results = await postgrest.rpc("get_users_with_fbos").select("*");
    setUsers(results.body);
  }, []);

  const onSave = React.useCallback(async () => {
    for (const action of actionsOnSave) {
      await action();
    }
    setActionsOnSave([]);
  }, [actionsOnSave]);

  React.useEffect(() => {
    setFBOFilter("");
  }, [editingUser]);

  const onChangeRole = React.useCallback(
    async (
      user_id: string,
      user_display_name: string,
      fbo_id: string,
      _role: string
    ) => {
      if (_role === "none") {
        await postgrest
          .from("fbo_employees")
          .delete()
          .eq("user_id", user_id)
          .eq("fbo_id", fbo_id);
      } else {
        // super annoying but postgres doesn't do upserts on non-primary keys
        await postgrest.rpc("modify_role", {
          _user_id: user_id,
          _fbo_id: fbo_id,
          __role: _role,
          _user_display_name: user_display_name,
        });
      }
      const results = await postgrest.rpc("get_users_with_fbos").select("*");
      setUsers(results.body);
    },
    []
  );

  const sortedFbos = React.useMemo(() => {
    return [...fbos].sort((a, b) => {
      const nameA = a.name.toLocaleLowerCase();
      const nameB = b.name.toLocaleLowerCase();
      if (nameA.indexOf(fboFilter.toLocaleLowerCase()) > -1) {
        return -1;
      }
      if (nameB.indexOf(fboFilter.toLocaleLowerCase()) > -1) {
        return 1;
      }
      return nameA.localeCompare(nameB);
    });
  }, [fbos, fboFilter]);

  if (loadingUsers) {
    return <div>{t("screens.superAdmin.superUserAdmin.loading")}</div>;
  }

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: t("screens.superAdmin.superUserAdmin.columns.userId"),
      width: 100,
      editable: false,
      valueGetter: (_, row) => row.id.slice(0, 7) || t("screens.superAdmin.superUserAdmin.placeholders.none"),
    },
    {
      field: "name",
      headerName: t("screens.superAdmin.superUserAdmin.columns.name"),
      width: 175,
      editable: false,
      valueGetter: (_, row) => row.name || t("screens.superAdmin.superUserAdmin.placeholders.none"),
    },
    {
      field: "email",
      headerName: t("screens.superAdmin.superUserAdmin.columns.email"),
      width: 250,
      editable: false,
      valueGetter: (_, row) => row.email || t("screens.superAdmin.superUserAdmin.placeholders.none"),
    },
    {
      field: "fbos",
      headerName: t("screens.superAdmin.superUserAdmin.columns.fbo"),
      width: 400,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          {params.row.fbos
            .sort((a, b) => a.name.localeCompare(b.name))
            .slice(0, 5)
            .map((fbo) => (
              <Chip
                key={fbo.id}
                size="small"
                variant="outlined"
                label={fbo.name}
              />
            ))}
          {params.row.fbos.length > 5 && (
            <Chip
              size="small"
              variant="outlined"
              label={t("screens.superAdmin.superUserAdmin.moreChip", { count: params.row.fbos.length - 5 })}
            />
          )}
        </Stack>
      ),
    },
    {
      field: "action",
      headerName: t("screens.superAdmin.superUserAdmin.columns.action"),
      width: 200,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1} alignItems="center" height="100%">
          <Button
            variant="contained"
            color="info"
            size="small"
            onClick={() => {
              setEditingUser(params.row.id);
              setActionsOnSave([]);
            }}
          >
            {t("screens.superAdmin.superUserAdmin.buttons.edit")}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Stack
        direction="column"
        spacing={2}
        sx={{ width: "100%", minWidth: 700, p: 1 }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={t("screens.superAdmin.superUserAdmin.placeholders.searchUser")}
          inputProps={{
            ["data-testid"]: "input_filter_team_member",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(evt) => setUserFilter(evt.target.value)}
        />
        <div style={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={users.filter(
              (u) =>
                `${u.email} ${u.name}`
                  .toLocaleLowerCase()
                  .indexOf(userFilter.toLocaleLowerCase()) > -1
            )}
            columns={columns}
            pageSizeOptions={[20]}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
          />
        </div>
      </Stack>
      <Dialog
        open={!!editingUser}
        onClose={() => {
          setEditingUser(null);
          setActionsOnSave([]);
        }}
      >
        <DialogTitle>
          <Typography variant="inherit">{t("screens.superAdmin.superUserAdmin.dialog.title")}</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={1} height={500} width={500}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("screens.superAdmin.superUserAdmin.placeholders.searchFBO")}
              inputProps={{
                ["data-testid"]: "input_filter_team_member",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(evt) => setFBOFilter(evt.target.value)}
            />
            <DataGrid
              rows={sortedFbos}
              columns={[
                {
                  field: "name",
                  headerName: t("screens.superAdmin.superUserAdmin.columns.name"),
                  width: 200,
                },
                {
                  field: "permissions",
                  headerName: t("screens.superAdmin.superUserAdmin.columns.permissions"),
                  width: 200,
                  renderCell: (params: GridRenderCellParams) => {
                    const user = users.find((u) => u.id === editingUser);
                    const userRole =
                      user?.fbo_employees.find(
                        (e) => e.fbo_id === params.row.id
                      )?._role || "none";
                    return (
                      <Stack
                        direction="column"
                        height="100%"
                        justifyContent="center"
                      >
                        <TextField
                          key={`${editingUser}-${params.row.id}`}
                          select
                          fullWidth
                          size="small"
                          value={userRole}
                          onChange={async (evt) => {
                            const newRole = evt.target.value;

                            // Optimistically update local state immediately
                            setUsers((prevUsers) =>
                              prevUsers.map((u) => {
                                if (u.id !== editingUser) return u;

                                const existingEmployee = u.fbo_employees.find(
                                  (e) => e.fbo_id === params.row.id
                                );

                                if (newRole === "none") {
                                  // Remove the employee entry
                                  return {
                                    ...u,
                                    fbo_employees: u.fbo_employees.filter(
                                      (e) => e.fbo_id !== params.row.id
                                    ),
                                  };
                                } else if (existingEmployee) {
                                  // Update existing role
                                  return {
                                    ...u,
                                    fbo_employees: u.fbo_employees.map((e) =>
                                      e.fbo_id === params.row.id
                                        ? { ...e, _role: newRole }
                                        : e
                                    ),
                                  };
                                } else {
                                  // Add new employee entry
                                  return {
                                    ...u,
                                    fbo_employees: [
                                      ...u.fbo_employees,
                                      { fbo_id: params.row.id, _role: newRole },
                                    ],
                                  };
                                }
                              })
                            );

                            // Queue the action for server persistence
                            setActionsOnSave([
                              ...actionsOnSave,
                              async () =>
                                await onChangeRole(
                                  user.id,
                                  user.email,
                                  params.row.id,
                                  newRole
                                ),
                            ]);
                          }}
                        >
                          <MenuItem value="none">{t("screens.superAdmin.superUserAdmin.roles.none")}</MenuItem>
                          <MenuItem value="fbo_admin">{ROLE_DISPLAY_NAMES.fbo_admin}</MenuItem>
                          <MenuItem value="manager">{ROLE_DISPLAY_NAMES.manager}</MenuItem>
                          <MenuItem value="operator">{ROLE_DISPLAY_NAMES.operator}</MenuItem>
                          <MenuItem value="csr">{ROLE_DISPLAY_NAMES.csr}</MenuItem>
                        </TextField>
                      </Stack>
                    );
                  },
                },
              ]}
              pageSizeOptions={[1000]}
              disableVirtualization
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditingUser(null);
              setActionsOnSave([]);
            }}
          >
            {t("screens.superAdmin.superUserAdmin.buttons.close")}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={async () => {
              setLoading(true);
              await onSave();
              setLoading(false);
              setEditingUser(null);
            }}
            disabled={loading}
          >
            {loading ? t("screens.superAdmin.superUserAdmin.buttons.saving") : t("screens.superAdmin.superUserAdmin.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
